import type {
	PrefetchableComponentType,
	PrefetchDependenciesContext,
	PrefetchDependency,
	PrefetchedUserData,
	PrefetchUserContext,
	PrefetchUserContextData,
} from 'src/shared/prefetch';

import { resolvePrefetchableComponent } from './resolveComponent';

function checkNestedUserData(
	data: PrefetchedUserData,
	component: PrefetchableComponentType,
	options: PrefetchUserContext,
): PrefetchedUserData {
	if (!data) {
		return data;
	}
	const nestedProps = Object.keys(data).filter(
		(key) => typeof data[key] === 'object' && data[key] !== null && key !== 'experiments',
	);
	if (nestedProps.length > 0) {
		const err = new Error(
			`[${component.displayName}] User data does not allow nested properties (except for 'experiments') to limit impact on server-caching`,
		);
		void options.utilityContext.errorReporter.report({
			error: err,
			params: {
				component: component.displayName,
				nestedProps,
			},
		});
		if (process.env.NODE_ENV !== 'production') {
			throw err;
		}
		return data;
	}
	return data;
}

export async function prefetchComponentUserData(
	comp: PrefetchDependency,
	options: PrefetchUserContext,
): Promise<PrefetchUserContextData> {
	const component = await resolvePrefetchableComponent(comp);

	if (!component.prefetchName) {
		return undefined;
	}

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const userDataPromise: Promise<PrefetchedUserData> = component.prefetchUserData
		? component.prefetchUserData(options)
		: Promise.resolve(undefined);
	// making sure that a rejection will not be unhandled (which would crash the app)
	// this can happen in rare cases because of timing issues
	// if the promise throws, it will still be thrown by this function since we await userDataPromise (without the catch)
	// eslint-disable-next-line promise/prefer-await-to-then
	userDataPromise.catch(() => {});
	const getUserData = async () => checkNestedUserData(await userDataPromise, component, options);

	const prefetchDependenciesOptions: PrefetchDependenciesContext = {
		l10n: options.l10n,
		path: options.path,
		params: options.params,
		query: options.query,
		session: options.session,
		getUserData,
	};
	const dependencies = await Promise.resolve(component.prefetchDependencies?.(prefetchDependenciesOptions) || []);
	const depData = await Promise.all([
		getUserData().then((data) => (data ? { [component.prefetchName]: data } : {})),
		...dependencies.map(async (dep) => prefetchComponentUserData(dep, options)),
	]);

	const combinedData = depData.reduce<PrefetchUserContextData>((acc, dep) => {
		const combined = {
			...acc,
			...dep,
		};
		// if the sum of keys is not the same as the combined object's # of keys,
		// it means that dep has keys that were already in acc, which is not supposed to happen
		// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/consistent-type-assertions
		if (Object.keys(acc as any).length + Object.keys(dep || {}).length !== Object.keys(combined).length) {
			throw new Error('Prefetched data overrides other prefetched data');
		}
		return combined;
	}, {});

	return combinedData;
}
